import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { SubscriptionSummary } from "../components/SubscriptionSummary";
import { ProfileSkillsDrawer } from "../components/ProfileSkillsDrawer";
import { resumeTypesService } from "../services/resumeTypesService";
import { ValidationUtils } from "../utils/validationUtils";
import { apiService } from "../services/apiService";
import toast from "react-hot-toast";
import { Trash2 } from "lucide-react";
import "./ProfilePage.css";

type ProfileTab = "profile" | "security" | "subscription";

const normalizeProfileText = (value?: string | null) =>
  value?.trim().replace(/\s+/g, " ") || "";

const buildSummarySections = (...values: Array<string | null | undefined>) => {
  const nextSections: string[] = [];
  const seen = new Set<string>();

  for (const rawValue of values) {
    const value = normalizeProfileText(rawValue);
    if (!value) {
      continue;
    }

    const key = value.toLowerCase();
    if (seen.has(key)) {
      continue;
    }

    seen.add(key);
    nextSections.push(value);
  }

  return nextSections;
};

const normalizeSkillList = (skills?: string[] | null) => {
  const nextSkills: string[] = [];
  const seen = new Set<string>();

  for (const rawSkill of skills || []) {
    const skill = normalizeProfileText(rawSkill);
    if (!skill) {
      continue;
    }

    const key = skill.toLowerCase();
    if (seen.has(key)) {
      continue;
    }

    seen.add(key);
    nextSkills.push(skill);
  }

  return nextSkills;
};

const skillListsMatch = (left?: string[] | null, right?: string[] | null) => {
  const normalizedLeft = normalizeSkillList(left);
  const normalizedRight = normalizeSkillList(right);

  if (normalizedLeft.length !== normalizedRight.length) {
    return false;
  }

  return normalizedLeft.every(
    (skill, index) => skill === normalizedRight[index],
  );
};

export const ProfilePage: React.FC = () => {
  const { profile, updateProfile, changePassword, logout, refreshProfile } =
    useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: profile?.firstName || "",
    lastName: profile?.lastName || "",
    email: profile?.email || "",
    phone: profile?.phone || "",
    linkedinUrl: profile?.linkedinUrl || "",
    industry: profile?.industry || "",
    summary: normalizeProfileText(profile?.summary),
  });
  const [industries, setIndustries] = useState<string[]>([]);
  const [loadingIndustries, setLoadingIndustries] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordErrors, setPasswordErrors] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<ProfileTab>("profile");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSkillsDrawerOpen, setIsSkillsDrawerOpen] = useState(false);
  const [isSavingSkills, setIsSavingSkills] = useState(false);
  const [pendingFocusField, setPendingFocusField] = useState<string | null>(
    null,
  );
  const navigate = useNavigate();

  const manualSummary = normalizeProfileText(profile?.summary);
  const generatedSummary = normalizeProfileText(profile?.generatedSummary);
  const manualSkills = normalizeSkillList(profile?.skills);
  const generatedSkills = normalizeSkillList(profile?.generatedSkills);
  const effectiveSummarySections = buildSummarySections(
    manualSummary,
    generatedSummary,
  );
  const effectiveSkills = normalizeSkillList([
    ...manualSkills,
    ...generatedSkills,
  ]);
  const isUsingGeneratedSummary = !manualSummary && !!generatedSummary;
  const isUsingGeneratedSkills =
    manualSkills.length === 0 && generatedSkills.length > 0;
  const summarySourceLabel =
    manualSummary && generatedSummary
      ? "Custom + resumes"
      : isUsingGeneratedSummary
        ? "From resumes"
        : manualSummary
          ? "Custom"
          : "Not set";
  const skillsSourceLabel =
    manualSkills.length > 0 && generatedSkills.length > 0
      ? "Custom + resumes"
      : isUsingGeneratedSkills
        ? "From resumes"
        : manualSkills.length > 0
          ? "Custom"
          : "Not set";

  // Handle tab state from navigation (e.g., from Settings redirect)
  useEffect(() => {
    if (
      location.state &&
      typeof location.state === "object" &&
      "tab" in location.state
    ) {
      const tab = location.state.tab as ProfileTab;
      if (tab === "profile" || tab === "security" || tab === "subscription") {
        setActiveTab(tab);
      }
    }
  }, [location.state]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const focusParam = params.get("focus");
    const editParam = params.get("edit") === "true";
    const normalizedFocus =
      focusParam === "linkedin" ? "linkedinUrl" : focusParam;

    const isValidFocus =
      normalizedFocus === "phone" ||
      normalizedFocus === "industry" ||
      normalizedFocus === "linkedinUrl";

    if (isValidFocus || editParam) {
      setActiveTab("profile");
    }

    if (editParam) {
      setIsEditing(true);
    }

    if (isValidFocus) {
      setPendingFocusField(normalizedFocus);
    }
  }, [location.search]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Load industries on component mount
  useEffect(() => {
    const loadIndustries = async () => {
      setLoadingIndustries(true);
      try {
        const industriesList = await resumeTypesService.getIndustries();
        setIndustries(industriesList);
      } catch (error) {
        console.error("Failed to load industries:", error);
      } finally {
        setLoadingIndustries(false);
      }
    };
    loadIndustries();
  }, []);

  // Sync formData when individual profile fields change (NOT object identity).
  const pFirstName = profile?.firstName;
  const pLastName = profile?.lastName;
  const pEmail = profile?.email;
  const pPhone = profile?.phone;
  const pLinkedin = profile?.linkedinUrl;
  const pIndustry = profile?.industry;
  const pSummary = profile?.summary;
  useEffect(() => {
    if (!profile) return;
    setFormData({
      firstName: pFirstName || "",
      lastName: pLastName || "",
      email: pEmail || "",
      phone: pPhone || "",
      linkedinUrl: pLinkedin || "",
      industry: pIndustry || "",
      summary: normalizeProfileText(pSummary),
    });
  }, [pFirstName, pLastName, pEmail, pPhone, pLinkedin, pIndustry, pSummary]);

  useEffect(() => {
    if (!pendingFocusField || !isEditing || activeTab !== "profile") return;
    const field = document.getElementById(pendingFocusField) as
      | HTMLInputElement
      | HTMLSelectElement
      | null;
    if (field) {
      field.scrollIntoView({ behavior: "smooth", block: "center" });
      field.focus();
    }
    setPendingFocusField(null);
  }, [pendingFocusField, isEditing, activeTab]);

  useEffect(() => {
    if (!profile?.id) return;

    refreshProfile().catch((refreshError) => {
      console.error("Failed to refresh profile on Profile page:", refreshError);
    });
  }, [profile?.id, refreshProfile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setMessage("");

    try {
      if (!profile) {
        setLoading(false);
        return;
      }

      const nextSummary = normalizeProfileText(formData.summary);
      const currentSummary = normalizeProfileText(profile.summary);
      const updates: Record<string, string> = {};

      if (formData.firstName !== (profile.firstName || "")) {
        updates.firstName = formData.firstName;
      }
      if (formData.lastName !== (profile.lastName || "")) {
        updates.lastName = formData.lastName;
      }
      if (formData.phone !== (profile.phone || "")) {
        updates.phone = formData.phone;
      }
      if (formData.linkedinUrl !== (profile.linkedinUrl || "")) {
        updates.linkedinUrl = formData.linkedinUrl;
      }
      if (formData.industry !== (profile.industry || "")) {
        updates.industry = formData.industry;
      }
      if (
        normalizeProfileText(nextSummary) !==
        normalizeProfileText(currentSummary)
      ) {
        updates.summary = nextSummary;
      }

      if (Object.keys(updates).length === 0) {
        setError("No changes to save");
        setLoading(false);
        return;
      }

      await updateProfile(updates);
      setMessage("Profile updated successfully!");
      setIsEditing(false);
    } catch (error) {
      console.error("Profile update error:", error);
      if (error instanceof TypeError && error.message.includes("fetch")) {
        setError("Network error. Please check your connection and try again.");
      } else {
        setError(
          error instanceof Error ? error.message : "Failed to update profile",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSkills = async (nextSkills: string[]) => {
    if (!profile) {
      return;
    }

    setIsSavingSkills(true);
    setError("");
    setMessage("");

    try {
      const normalizedDraftSkills = normalizeSkillList(nextSkills);

      if (skillListsMatch(normalizedDraftSkills, manualSkills)) {
        setIsSkillsDrawerOpen(false);
        return;
      }

      await updateProfile({ skills: normalizedDraftSkills });
      setMessage("Profile skills updated successfully!");
      setIsSkillsDrawerOpen(false);
    } catch (saveError) {
      const errorMessage =
        saveError instanceof Error
          ? saveError.message
          : "Failed to update profile skills";
      setError(errorMessage);
    } finally {
      setIsSavingSkills(false);
    }
  };

  const handleResetSkillsToGenerated = async () => {
    setIsSavingSkills(true);
    setError("");
    setMessage("");

    try {
      if (skillListsMatch(manualSkills, [])) {
        setIsSkillsDrawerOpen(false);
        return;
      }

      await updateProfile({ skills: [] });
      setMessage(
        "Custom profile skills cleared. Resume-derived skills remain appended.",
      );
      setIsSkillsDrawerOpen(false);
    } catch (resetError) {
      const errorMessage =
        resetError instanceof Error
          ? resetError.message
          : "Failed to reset profile skills";
      setError(errorMessage);
    } finally {
      setIsSavingSkills(false);
    }
  };

  const validatePassword = (password: string): string => {
    const validation = ValidationUtils.validatePassword(password);
    return validation.isValid ? "" : validation.error || "";
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Validate in real-time
    if (name === "newPassword") {
      const error = validatePassword(value);
      setPasswordErrors((prev) => ({
        ...prev,
        newPassword: error,
      }));
    } else if (name === "confirmPassword") {
      const error =
        passwordData.newPassword !== value ? "Passwords do not match" : "";
      setPasswordErrors((prev) => ({
        ...prev,
        confirmPassword: error,
      }));
    }

    // Clear general error when user types
    if (error) setError("");
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setMessage("");

    // Validate new password
    const passwordValidation = ValidationUtils.validatePassword(
      passwordData.newPassword,
    );
    if (!passwordValidation.isValid) {
      const errorMsg =
        passwordValidation.error || "Password does not meet requirements";
      setError(errorMsg);
      setPasswordErrors((prev) => ({
        ...prev,
        newPassword: errorMsg,
      }));
      toast.error(errorMsg, {
        duration: 5000,
        position: "top-right",
      });
      setLoading(false);
      return;
    }

    // Validate password match
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      const errorMsg = "New passwords do not match";
      setError(errorMsg);
      setPasswordErrors((prev) => ({
        ...prev,
        confirmPassword: errorMsg,
      }));
      toast.error(errorMsg, {
        duration: 4000,
        position: "top-right",
      });
      setLoading(false);
      return;
    }

    // Validate current password is provided
    if (!passwordData.currentPassword.trim()) {
      const errorMsg = "Current password is required";
      setError(errorMsg);
      toast.error(errorMsg, {
        duration: 4000,
        position: "top-right",
      });
      setLoading(false);
      return;
    }

    try {
      await changePassword(
        passwordData.currentPassword,
        passwordData.newPassword,
      );
      const successMsg = "Password changed successfully!";
      setMessage(successMsg);
      toast.success(successMsg, {
        duration: 4000,
        position: "top-right",
      });
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setPasswordErrors({ newPassword: "", confirmPassword: "" });
      setShowPasswordForm(false);
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : "Failed to change password";
      setError(errorMsg);
      toast.error(errorMsg, {
        duration: 5000,
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!profile) {
    return (
      <div className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl font-extrabold text-gray-900">
              Profile Not Found
            </h1>
            <p className="mt-2 text-sm text-gray-600">
              Please log in to view your profile
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handleDeleteAccount = async () => {
    if (!showDeleteConfirm) {
      setShowDeleteConfirm(true);
      return;
    }

    setIsDeleting(true);
    try {
      await apiService.delete("/profiles/me");

      // Clear auth state
      logout();

      // Clear local storage
      localStorage.removeItem("token");
      localStorage.removeItem("userProfile");
      localStorage.removeItem("authToken");

      // Show success message
      toast.success("Your account has been permanently deleted");

      // Redirect to login
      setTimeout(() => {
        navigate("/login");
      }, 1000);
    } catch (error: any) {
      console.error("Error deleting account:", error);
      const errorMessage =
        error?.message || "Failed to delete account. Please try again.";
      toast.error(errorMessage);
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className="py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center" data-tour-id="profile-header">
          <h1 className="text-3xl font-extrabold text-gray-900">Profile</h1>
          <p className="mt-2 text-sm text-gray-600">
            Manage your profile information, security, and subscription
          </p>
        </div>

        {/* Tabs Navigation */}
        <div
          className="mt-8 profile-tabs-container"
          data-tour-id="profile-tabs"
        >
          <div className="profile-tabs">
            <button
              className={`profile-tab ${activeTab === "profile" ? "active" : ""}`}
              onClick={() => setActiveTab("profile")}
              data-tour-id="profile-tab-profile"
            >
              Profile
            </button>
            <button
              className={`profile-tab ${activeTab === "security" ? "active" : ""}`}
              onClick={() => setActiveTab("security")}
              data-tour-id="profile-tab-security"
            >
              Security
            </button>
            <button
              className={`profile-tab ${activeTab === "subscription" ? "active" : ""}`}
              onClick={() => setActiveTab("subscription")}
              data-tour-id="profile-tab-subscription"
            >
              Subscription
            </button>
          </div>
        </div>

        <div className="mt-6 space-y-6">
          {error && <div className="alert alert-error">{error}</div>}

          {message && <div className="alert alert-success">{message}</div>}

          {/* Profile Tab */}
          {activeTab === "profile" && (
            <>
              <div
                className="card profile-card"
                data-tour-id="profile-basic-info"
              >
                <div className="card-header">
                  <h2 className="card-title">Profile Information</h2>
                  {!isEditing && (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="btn btn-primary profile-action-btn"
                      data-tour-id="profile-edit-button"
                    >
                      Edit
                    </button>
                  )}
                </div>
                <div className="card-content">
                  {isEditing ? (
                    <form onSubmit={handleSubmit} className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="form-group">
                          <label htmlFor="firstName" className="form-label">
                            First Name
                          </label>
                          <input
                            id="firstName"
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            className="form-input"
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor="lastName" className="form-label">
                            Last Name
                          </label>
                          <input
                            id="lastName"
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            className="form-input"
                            required
                          />
                        </div>
                      </div>

                      <div
                        className="grid grid-cols-1 md:grid-cols-2 gap-3"
                        data-tour-id="profile-contact"
                      >
                        <div className="form-group">
                          <label htmlFor="email" className="form-label">
                            Email
                          </label>
                          <input
                            id="email"
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className="form-input"
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor="phone" className="form-label">
                            Phone
                          </label>
                          <input
                            id="phone"
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className="form-input"
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="linkedinUrl" className="form-label">
                          LinkedIn Profile
                        </label>
                        <input
                          id="linkedinUrl"
                          type="url"
                          name="linkedinUrl"
                          value={formData.linkedinUrl}
                          onChange={handleInputChange}
                          className="form-input"
                          placeholder="https://www.linkedin.com/in/yourprofile"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Your LinkedIn profile URL will be used in generated
                          email communications.
                        </p>
                      </div>

                      <div className="form-group">
                        <label htmlFor="industry" className="form-label">
                          Industry
                        </label>
                        <select
                          id="industry"
                          name="industry"
                          value={formData.industry}
                          onChange={handleInputChange}
                          className="form-input"
                        >
                          <option value="">Select your industry</option>
                          {loadingIndustries ? (
                            <option disabled>Loading industries...</option>
                          ) : (
                            industries.map((industry) => (
                              <option key={industry} value={industry}>
                                {industry}
                              </option>
                            ))
                          )}
                        </select>
                        <p className="mt-1 text-xs text-gray-500">
                          Select your industry to see relevant resume types when
                          uploading resumes.
                        </p>
                      </div>

                      <div className="form-group">
                        <label htmlFor="summary" className="form-label">
                          Professional Summary
                        </label>
                        <textarea
                          id="summary"
                          name="summary"
                          value={formData.summary}
                          onChange={handleInputChange}
                          className="form-input profile-summary-input"
                          rows={5}
                          placeholder="Add a short professional summary for your profile."
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Leave this blank to use only the summary gathered from
                          your uploaded resumes. If you add custom text, the
                          resume-derived summary stays appended below it.
                        </p>
                      </div>

                      <div
                        className="flex space-x-3"
                        data-tour-id="profile-save-button"
                      >
                        <button
                          type="submit"
                          disabled={loading}
                          className="btn btn-primary"
                        >
                          {loading ? "Saving..." : "Save"}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setIsEditing(false);
                            setFormData({
                              firstName: profile.firstName || "",
                              lastName: profile.lastName || "",
                              email: profile.email || "",
                              phone: profile.phone || "",
                              linkedinUrl: profile.linkedinUrl || "",
                              industry: profile.industry || "",
                              summary: normalizeProfileText(profile.summary),
                            });
                          }}
                          className="btn btn-secondary"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  ) : (
                    <>
                      <div className="profile-display-grid">
                        <div className="profile-field">
                          <span className="profile-field-label">
                            First Name
                          </span>
                          <span className="profile-field-value">
                            {profile.firstName}
                          </span>
                        </div>
                        <div className="profile-field">
                          <span className="profile-field-label">Last Name</span>
                          <span className="profile-field-value">
                            {profile.lastName}
                          </span>
                        </div>
                        <div className="profile-field">
                          <span className="profile-field-label">Email</span>
                          <span className="profile-field-value">
                            {profile.email}
                          </span>
                        </div>
                        {profile.phone && (
                          <div className="profile-field">
                            <span className="profile-field-label">Phone</span>
                            <span className="profile-field-value">
                              {profile.phone}
                            </span>
                          </div>
                        )}
                        {profile.linkedinUrl && (
                          <div className="profile-field">
                            <span className="profile-field-label">
                              LinkedIn
                            </span>
                            <span className="profile-field-value">
                              <a
                                href={profile.linkedinUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 underline"
                              >
                                {profile.linkedinUrl}
                              </a>
                            </span>
                          </div>
                        )}
                        <div className="profile-field">
                          <span className="profile-field-label">Industry</span>
                          <span className="profile-field-value">
                            {profile.industry || "Not set"}
                          </span>
                        </div>
                      </div>
                      <div className="profile-summary-display">
                        <div className="profile-summary-display__header">
                          <span className="profile-field-label">
                            Professional Summary
                          </span>
                          <span
                            className={`profile-source-badge ${isUsingGeneratedSummary ? "generated" : "custom"}`}
                          >
                            {summarySourceLabel}
                          </span>
                        </div>
                        {effectiveSummarySections.length > 0 ? (
                          effectiveSummarySections.map((summarySection) => (
                            <p
                              key={summarySection}
                              className="profile-summary-display__text"
                            >
                              {summarySection}
                            </p>
                          ))
                        ) : (
                          <p className="profile-empty-copy">
                            No professional summary yet. Upload resumes or add a
                            custom summary.
                          </p>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="card profile-card generated-profile-card">
                <div className="card-header">
                  <div>
                    <h2 className="card-title">Skills</h2>
                    <p className="card-description">
                      Use the skills gathered from your resumes automatically,
                      or save a custom profile skill set.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsSkillsDrawerOpen(true)}
                    className="btn btn-primary profile-action-btn"
                  >
                    Edit Skills
                  </button>
                </div>
                <div className="card-content generated-profile-content">
                  <div className="generated-profile-section">
                    <div className="profile-summary-display__header">
                      <span className="generated-profile-label">
                        Current Profile Skills
                      </span>
                      <span
                        className={`profile-source-badge ${isUsingGeneratedSkills ? "generated" : "custom"}`}
                      >
                        {skillsSourceLabel}
                      </span>
                    </div>
                    {effectiveSkills.length > 0 ? (
                      <div className="generated-skills-list">
                        {effectiveSkills.map((skill) => (
                          <span key={skill} className="generated-skill-chip">
                            {skill}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="profile-empty-copy">
                        No skills available yet. Upload resumes or add custom
                        skills to your profile.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Security Tab */}
          {activeTab === "security" && (
            <>
              {/* Password Change */}
              <div
                className="card security-card"
                data-tour-id="profile-security"
              >
                <div className="card-header">
                  <h2 className="card-title">Security</h2>
                  {!showPasswordForm && (
                    <button
                      onClick={() => setShowPasswordForm(true)}
                      className="btn btn-primary security-action-btn"
                      data-tour-id="profile-change-password"
                    >
                      Change Password
                    </button>
                  )}
                </div>
                <div className="card-content">
                  {showPasswordForm ? (
                    <form
                      onSubmit={handlePasswordSubmit}
                      className="space-y-3"
                      data-help-exempt
                    >
                      {/* Password Requirements Info */}
                      <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
                        <p className="text-sm font-medium text-blue-900 mb-2">
                          Password Requirements:
                        </p>
                        <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                          <li>At least 8 characters long</li>
                          <li>At least one uppercase letter (A-Z)</li>
                          <li>At least one lowercase letter (a-z)</li>
                          <li>At least one number (0-9)</li>
                          <li>At least one special character (@$!%*?&)</li>
                        </ul>
                      </div>

                      <div
                        className="grid grid-cols-1 md:grid-cols-3 gap-3"
                        data-help-exempt
                      >
                        <div className="form-group">
                          <label
                            htmlFor="currentPassword"
                            className="form-label"
                          >
                            Current Password
                          </label>
                          <input
                            id="currentPassword"
                            type="password"
                            name="currentPassword"
                            value={passwordData.currentPassword}
                            onChange={handlePasswordChange}
                            className="form-input"
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="newPassword" className="form-label">
                            New Password
                          </label>
                          <input
                            id="newPassword"
                            type="password"
                            name="newPassword"
                            value={passwordData.newPassword}
                            onChange={handlePasswordChange}
                            className={`form-input ${passwordErrors.newPassword ? "border-red-500" : ""}`}
                            required
                            minLength={8}
                          />
                          {passwordErrors.newPassword && (
                            <p className="mt-1 text-xs text-red-600">
                              {passwordErrors.newPassword}
                            </p>
                          )}
                        </div>

                        <div className="form-group">
                          <label
                            htmlFor="confirmPassword"
                            className="form-label"
                          >
                            Confirm Password
                          </label>
                          <input
                            id="confirmPassword"
                            type="password"
                            name="confirmPassword"
                            value={passwordData.confirmPassword}
                            onChange={handlePasswordChange}
                            className={`form-input ${passwordErrors.confirmPassword ? "border-red-500" : ""}`}
                            required
                            minLength={8}
                          />
                          {passwordErrors.confirmPassword && (
                            <p className="mt-1 text-xs text-red-600">
                              {passwordErrors.confirmPassword}
                            </p>
                          )}
                        </div>
                      </div>

                      {error && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-3">
                          <p className="text-sm text-red-800">{error}</p>
                        </div>
                      )}

                      <div className="flex space-x-3">
                        <button
                          type="submit"
                          disabled={
                            loading ||
                            !!passwordErrors.newPassword ||
                            !!passwordErrors.confirmPassword
                          }
                          className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loading ? "Changing..." : "Save Password"}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowPasswordForm(false);
                            setPasswordData({
                              currentPassword: "",
                              newPassword: "",
                              confirmPassword: "",
                            });
                            setPasswordErrors({
                              newPassword: "",
                              confirmPassword: "",
                            });
                            setError("");
                          }}
                          className="btn btn-secondary"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div className="text-sm text-gray-500">
                      Click "Change Password" above to update your password.
                    </div>
                  )}
                </div>
              </div>

              {/* Delete Account */}
              <div
                className="card danger-zone-card"
                data-tour-id="profile-delete-account"
              >
                <div className="card-header">
                  <h2 className="card-title danger-zone-title">Danger Zone</h2>
                  <p className="card-description">Irreversible actions</p>
                </div>
                <div className="card-content">
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-1">
                        Delete Account
                      </h4>
                      <p className="text-sm text-gray-500 mb-3">
                        Permanently delete your account and all associated data.
                        This action cannot be undone.
                      </p>
                      {!showDeleteConfirm ? (
                        <button
                          onClick={() => setShowDeleteConfirm(true)}
                          className="btn btn-outline text-red-600 border-red-600 hover:bg-red-50 flex items-center gap-2"
                        >
                          <Trash2 size={16} />
                          Delete Account
                        </button>
                      ) : (
                        <div className="space-y-3">
                          <div className="bg-red-50 border border-red-200 rounded-md p-3">
                            <p className="text-sm text-red-800 font-medium mb-1">
                              Are you absolutely sure?
                            </p>
                            <p className="text-xs text-red-700">
                              This will permanently delete your account, all
                              resumes, job applications, and associated data.
                              This action cannot be undone.
                            </p>
                          </div>
                          <div className="flex space-x-3">
                            <button
                              onClick={handleDeleteAccount}
                              disabled={isDeleting}
                              className="btn bg-red-600 text-white hover:bg-red-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Trash2 size={16} />
                              {isDeleting
                                ? "Deleting..."
                                : "Yes, Delete My Account"}
                            </button>
                            <button
                              onClick={() => setShowDeleteConfirm(false)}
                              className="btn btn-secondary"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Subscription Tab */}
          {activeTab === "subscription" && (
            <div
              className="card subscription-card"
              data-tour-id="profile-subscription"
            >
              <div className="card-header">
                <h2 className="card-title">Subscription & Service Plan</h2>
              </div>
              <div className="card-content">
                <SubscriptionSummary />
              </div>
            </div>
          )}
        </div>
      </div>
      <ProfileSkillsDrawer
        isOpen={isSkillsDrawerOpen}
        manualSkills={manualSkills}
        generatedSkills={generatedSkills}
        isSaving={isSavingSkills}
        onClose={() => setIsSkillsDrawerOpen(false)}
        onSave={handleSaveSkills}
        onClearCustomSkills={
          manualSkills.length > 0 ? handleResetSkillsToGenerated : null
        }
      />
    </div>
  );
};
